import { useState, useEffect } from "react";
import axios from "axios";
import scraperApi from "api";
import type { CostCalculationProject } from "providers/HostedScrapingProvider/types";

export type ProjectCostSourceProduct = "api" | "api_playground" | "async" | "hosted";

/** Shape returned by the `projectCost` endpoint (otherwise untyped via scraperFetch). */
export interface ProjectCostResponse {
  cost?: number;
  inputLength?: number;
  errorMessages?: string[];
}

export interface ProjectCostEstimate {
  cost: number | undefined;
  inputLength: number;
  errorMessages: string[];
  isLoading: boolean;
}

const EMPTY_ESTIMATE: ProjectCostEstimate = {
  cost: undefined,
  inputLength: 0,
  errorMessages: [],
  isLoading: false,
};

/**
 * Shared cost-estimate hook for the `projectCost` endpoint. Owns the request
 * lifecycle (debounce, abort, loading) so each surface (crawler, hosted
 * scraping, API playground) doesn't reimplement it.
 *
 * Pass `undefined` for `project` when there's nothing to price (e.g. empty
 * input) and no request fires. The request only repeats when a cost-relevant
 * field changes — `input`, `config.type`, `config.apiParams` or `language` — so
 * unrelated and volatile fields (e.g. notificationConfig.notificationChanged)
 * don't retrigger.
 */
export function useProjectCostEstimate(
  project: CostCalculationProject | undefined,
  sourceProduct: ProjectCostSourceProduct,
  { debounceMs = 300 }: { debounceMs?: number } = {}
): ProjectCostEstimate {
  const [estimate, setEstimate] = useState<ProjectCostEstimate>(EMPTY_ESTIMATE);

  const costKey = project
    ? JSON.stringify({
        input: project.input,
        type: project.config?.type,
        apiParams: project.config?.apiParams,
        language: project.language,
      })
    : "";
  useEffect(() => {
    if (!project || !costKey) {
      setEstimate(EMPTY_ESTIMATE);
      return;
    }

    // Flag loading now and abort on any change, so a stale response can't land.
    setEstimate((prev) => (prev.isLoading ? prev : { ...prev, isLoading: true }));
    const controller = new AbortController();

    const timer = setTimeout(() => {
      scraperApi.hostedScraping
        .projectCost(project, sourceProduct, { signal: controller.signal })
        .then((response: ProjectCostResponse | null) => {
          if (response) {
            setEstimate({
              cost: response.cost,
              inputLength: response.inputLength ?? 0,
              errorMessages: response.errorMessages ?? [],
              isLoading: false,
            });
          }
        })
        .catch((err) => {
          if (axios.isCancel(err)) return;
          console.error("Cost calculation error:", err);
          setEstimate({ cost: undefined, inputLength: 0, errorMessages: ["Network error"], isLoading: false });
        });
    }, debounceMs);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costKey, sourceProduct]);

  return estimate;
}
